import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { createJsonFetch, listDocumentTypes, patchDocumentType } from './settings.api';
import type { DocumentTypeDefinition, JsonFetch } from './settings.api';

function useScreenFetch(): JsonFetch {
  const { authorizedFetch } = useAuth();
  return useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);
}

export function DocumentTypesView({
  types,
  error,
  onToggleApproval,
}: {
  types: readonly DocumentTypeDefinition[];
  error?: string | null;
  onToggleApproval?: (code: string, enabled: boolean) => void;
}): JSX.Element {
  const { t, language } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-document-types">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {types.length === 0 ? <p className="muted">{t('settings.empty')}</p> : null}
      {types.map((type) => (
        <article key={type.code} className="settings__card" data-testid="document-type" data-code={type.code}>
          <header className="settings__card-head">
            <strong>{type.nameI18n[language]}</strong>
            <span className="mono muted">{type.code}</span>
          </header>

          <div className="settings__row">
            <span className="muted">{t('documentTypes.headerFields')}</span>
            <span className="settings__chips">
              {type.fields.map((field) => (
                <span key={field.name} className="chip" data-field={field.name}>
                  {field.name} · {field.type}
                  {field.required === true ? ' *' : ''}
                </span>
              ))}
            </span>
          </div>

          <div className="settings__row">
            <span className="muted">{t('documentTypes.tableFields')}</span>
            <span className="settings__chips">
              {type.tableFields.fields.map((field) => (
                <span key={field.name} className="chip" data-table-field={field.name}>
                  {field.name} · {field.type}
                </span>
              ))}
            </span>
          </div>

          <div
            className="settings__row"
            data-testid="status-model"
            data-status-model={type.statusModel.statuses.join(',')}
            data-initial-status={type.statusModel.initial}
          >
            <span className="muted">{t('documentTypes.statusModel')}</span>
            <span className="settings__chips">
              {type.statusModel.statuses.map((status) => (
                <span key={status} className="chip">
                  {t(`settings.docStatus.${status}`)}
                </span>
              ))}
            </span>
            <span className="muted">
              {type.statusModel.transitions
                .map((transition) => `${transition.from}→${transition.to}`)
                .join(', ')}
            </span>
          </div>

          <label className="settings__row" data-approval-enabled={type.statusModel.approvalEnabled ? 'true' : 'false'}>
            <span>{t('documentTypes.approvalEnabled')}</span>
            <input
              type="checkbox"
              checked={type.statusModel.approvalEnabled}
              aria-label={t('documentTypes.approvalEnabled')}
              onChange={(event) => onToggleApproval?.(type.code, event.currentTarget.checked)}
            />
          </label>
        </article>
      ))}
    </div>
  );
}

export default function DocumentTypesScreen(): JSX.Element {
  const { t } = useI18n();
  const fetchJson = useScreenFetch();
  const [types, setTypes] = useState<DocumentTypeDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    try {
      setTypes(await listDocumentTypes(fetchJson));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [fetchJson]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggle = async (code: string, enabled: boolean): Promise<void> => {
    try {
      await patchDocumentType(fetchJson, code, { approvalEnabled: enabled });
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  if (types === null) {
    return (
      <section className="card" data-testid="document-types-loading">
        <p className="muted">{t('settings.loading')}</p>
      </section>
    );
  }

  return (
    <div className="settings__screen">
      <header className="page-head">
        <div>
          <h1>{t('documentTypes.title')}</h1>
          <p className="muted">{t('documentTypes.subtitle')}</p>
        </div>
      </header>
      <DocumentTypesView types={types} error={error} onToggleApproval={(code, enabled) => void toggle(code, enabled)} />
    </div>
  );
}
